import { useQueryClient } from "@tanstack/react-query";
import type React from "react";
import { useState } from "react";
import { Query } from "src/types/query";

import {
    useQueryNotifications,
    useSawNotification,
    useSawNotifications,
} from "src/api/notifications";
import useAuth from "src/hooks/useAuth";
import type { Notification } from "src/types/notification";

interface NotificationsHook {
    notifications: Notification[];
    anchorEl?: HTMLElement;
    displayOnlyUnseenNotifications: boolean;
    handleOpen: (event: React.MouseEvent<HTMLButtonElement>) => void;
    handleClose: () => void;
    handleSawNotifications: (notifications: Notification[]) => Promise<void>;
    handleNotificationClick: (notification: Notification) => () => Promise<void>;
    handleUnseenSwitch: () => void;
    disableAction: (event) => void;
}

export const useNotifications = (): NotificationsHook => {
    const clientQuery = useQueryClient();
    const { user } = useAuth();

    const { mutateAsync: sawNotification } = useSawNotification();
    const { mutateAsync: sawNotifications } = useSawNotifications();

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const { data } = useQueryNotifications();
    let notifications = data || [];

    const [displayOnlyUnseenNotifications, setDisplayOnlyUnseenNotifications] = useState(false);

    const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        clientQuery.invalidateQueries({
            queryKey: [Query.Notifications, user.id],
        });
        setAnchorEl(null);
    };

    const handleNotificationClick =
        (notification: Notification, hasToNotClose?: boolean) => async () => {
            !hasToNotClose && handleClose();
            await sawNotification(notification.id);
        };

    const handleSawNotifications = async (notifications: [Notification]) => {
        await sawNotifications(notifications);
    };

    const handleUnseenSwitch = () =>
        setDisplayOnlyUnseenNotifications(!displayOnlyUnseenNotifications);

    const disableAction = (event) => {
        event.stopPropagation();
    };

    if (displayOnlyUnseenNotifications) {
        notifications = notifications.filter((notification) => !notification.seenDateUTC);
    }
    return {
        notifications,
        anchorEl,
        displayOnlyUnseenNotifications,
        handleOpen,
        handleClose,
        handleNotificationClick,
        handleSawNotifications,
        handleUnseenSwitch,
        disableAction,
    };
};
